import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Banknote, CalendarClock } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Brand } from "@/components/Shell";

function Choice({ testid, icon: I, title, text, onClick, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="flex w-full items-start gap-4 rounded-3xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-emerald-500 disabled:opacity-50">
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-700"><I className="h-6 w-6" /></span>
      <span><span className="block font-display text-lg font-bold text-slate-900">{title}</span><span className="text-sm text-slate-600">{text}</span></span>
    </button>
  );
}

export default function CompensationPage() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/appointments/${aid}/compensation`).then(({ data: d }) => setData(d)).catch((e) => setError(errMsg(e)));
  }, [aid]);

  const choose = async (choice) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/appointments/${aid}/compensation`, { choice });
      if (choice === "rimborso") {
        toast.success(`Rimborso di ${r.refund_amount} € richiesto: la segreteria lo conferma a breve`);
        navigate("/area-personale");
      } else {
        toast.success(`Sconto del ${r.pct}% attivato (codice ${r.voucher_code}): scegli ora il nuovo orario`);
        navigate(`/riprogramma/${aid}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const a = data?.appointment;
  const done = data && data.compensation.status !== "proposta";
  return (
    <div className="min-h-screen bg-emerald-50/40 px-4 py-6" data-testid="compensation-page">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 flex items-center gap-3"><Link to="/area-personale" data-testid="compensation-back" className="grid h-10 w-10 place-items-center rounded-full bg-white text-slate-700 shadow-sm"><ArrowLeft className="h-4 w-4" /></Link><Brand /></div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Ci scusiamo per il disagio</h1>
        {error && <p className="mt-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="compensation-error">{error}</p>}
        {a && (
          <>
            <p className="mt-2 text-sm text-slate-600" data-testid="compensation-summary">Il professionista non è entrato nel {a.service_name} con {a.professional_name} del {fmtDate(data.compensation.missed_date || a.date)} alle {data.compensation.missed_time || a.time}.{!done && " Scegli come preferisci rimediare:"}</p>
            {done ? (
              <p className="mt-6 rounded-2xl bg-white p-4 text-sm text-slate-700" data-testid="compensation-done">
                {data.compensation.status === "rimborso_scelto" ? "Hai già scelto il rimborso totale: la segreteria lo sta gestendo." : `Hai già scelto lo sconto (codice ${data.compensation.voucher_code}).`}
              </p>
            ) : (
              <div className="mt-6 space-y-3">
                <Choice testid="compensation-refund" icon={Banknote} disabled={busy || a.payment_status !== "pagato"} onClick={() => choose("rimborso")}
                  title={`Rimborso totale · ${a.amount} €`} text="Il consulto viene annullato e ti restituiamo l'intero importo pagato." />
                <Choice testid="compensation-discount" icon={CalendarClock} disabled={busy} onClick={() => choose("sconto")}
                  title={`Nuovo orario + sconto ${data.discount_pct}%`} text={`Scegli subito un nuovo orario con lo stesso professionista (il pagamento resta valido) e ricevi uno sconto del ${data.discount_pct}% sulla prossima visita, valido 6 mesi.`} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
